import { tokenManager } from "../utils/tokenManager";
import { userTimezoneHeaders } from "../utils/userTimezoneHeader";
import { handleAuthApiFailure } from "../utils/authSessionFailure.js";


export class ApiRequestError extends Error {
  code: string;

  /** HTTP status when the failure came from `fetch` (0 if unknown). */
  status: number;

  constructor(message: string, code = "REQUEST_FAILED", status = 0) {
    super(message);
    this.name = "ApiRequestError";
    this.code = code;
    this.status = status;
  }
}

export function resolveDynamicHost(rawUrl: string): string {
  if (!rawUrl) return rawUrl;
  const s = String(rawUrl).trim().replace(/\/$/, "");
  if (typeof window === "undefined" || !window.location?.hostname) return s;
  try {
    const urlObj = new URL(s);
    if (urlObj.hostname === "localhost" || urlObj.hostname === "127.0.0.1" || urlObj.hostname === "0.0.0.0") {
      urlObj.hostname = window.location.hostname;
      return urlObj.toString().replace(/\/$/, "");
    }
  } catch (e) {
    // ignore
  }
  return s;
}

export function getOmniWatchBaseUrl(): string {
  const raw =
    process.env.REACT_APP_WAHANKHEDE_360_API_URL ||
    process.env.REACT_APP_OMNI_WATCH_API_URL ||
    "";
  return resolveDynamicHost(raw);
}

export function getAiDetectionApiBaseUrl(): string {
  const raw = process.env.REACT_APP_API_URL_FOR_AI_DETECTION || "";
  return resolveDynamicHost(raw);
}

/** Adonis proxy: `/api/streams/hls/...` (not legacy `/api/v1/streams/...`). */
export function normalizeStreamMediaPath(path: string): string {
  return String(path).replace(/\/api\/v1\/streams\//gi, "/api/streams/");
}

function isStreamOrRecordingPath(path: string): boolean {
  const p = normalizeStreamMediaPath(path);
  return (
    p.startsWith("/api/streams") || p.startsWith("/api/recordings")
  );
}

function streamApiBaseUrl(): string {
  const node =
    process.env.REACT_APP_NODE_API_URL?.trim().replace(/\/$/, "") || "";
  const omni = getOmniWatchBaseUrl();
  return resolveDynamicHost(node) || omni;
}

/**
 * Only CRA `npm start` has `package.json` `"proxy"` → Node.
 * Production static hosts (`serve`, NGINX without /api proxy) must NOT use
 * the page origin for HLS — that returns SPA HTML instead of playlists.
 */
function shouldResolveStreamsViaPageOrigin(relativePath: string): boolean {
  if (process.env.NODE_ENV !== "development") return false;
  if (typeof window === "undefined" || !window.location?.origin) return false;
  if (!isStreamOrRecordingPath(relativePath)) return false;
  const configuredBase = streamApiBaseUrl();
  if (!configuredBase) return true;
  try {
    const apiOrigin = new URL(configuredBase).origin;
    const pageOrigin = window.location.origin;
    if (apiOrigin === pageOrigin) return false;
    const isLocal = (o: string) => {
      const h = new URL(o).hostname;
      return h === "localhost" || h === "127.0.0.1" || h === "0.0.0.0";
    };
    return isLocal(apiOrigin) && isLocal(pageOrigin);
  } catch {
    return false;
  }
}

/**
 * Resolve stream/snapshot paths for HLS.js (playlists + segments).
 * - `npm start`: same-origin `/api/streams/...` (CRA proxy).
 * - `serve` / production: absolute `REACT_APP_NODE_API_URL` (or Omni base).
 */
export function resolveOmniMediaUrl(
  pathOrAbsolute: string | null | undefined,
): string {
  if (pathOrAbsolute == null) return "";
  const s = String(pathOrAbsolute).trim();
  if (!s) return "";

  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      u.pathname = normalizeStreamMediaPath(u.pathname);
      if (
        isStreamOrRecordingPath(u.pathname) &&
        shouldResolveStreamsViaPageOrigin(u.pathname)
      ) {
        return `${window.location.origin}${u.pathname}${u.search}`;
      }
      return u.toString();
    } catch {
      return s;
    }
  }

  const p = normalizeStreamMediaPath(s.startsWith("/") ? s : `/${s}`);
  if (shouldResolveStreamsViaPageOrigin(p)) {
    return `${window.location.origin}${p}`;
  }

  const base = streamApiBaseUrl();
  if (base) {
    return `${base}${p}`;
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}${p}`;
  }
  return p;
}

/**
 * JSON API base for Omni Watch / cameras / PTZ via `apiGet` / `apiSend`.
 * Prefers dedicated Omni URL, then `REACT_APP_NODE_API_URL`.
 */
function jsonApiBaseUrl(): string {
  return getOmniWatchBaseUrl() || streamApiBaseUrl();
}

/**
 * CRA `npm start` only: use relative `/api/...` so package.json proxy hits Node.
 * `serve` / production must use an absolute Node URL (no proxy).
 */
function shouldUseDevProxyForJsonApi(base: string): boolean {
  if (process.env.NODE_ENV !== "development") return false;
  if (!base) return true;
  if (typeof window === "undefined" || !window.location?.origin) return false;
  try {
    const apiOrigin = new URL(base).origin;
    const pageOrigin = window.location.origin;
    if (apiOrigin === pageOrigin) return false;
    const isLocal = (o: string) => {
      const h = new URL(o).hostname;
      return h === "localhost" || h === "127.0.0.1" || h === "0.0.0.0";
    };
    return isLocal(apiOrigin) && isLocal(pageOrigin);
  } catch {
    return false;
  }
}

/**
 * Request URL for Omni Watch / Node JSON APIs (cameras, PTZ, etc.).
 * - `npm start`: relative path → CRA proxy
 * - `serve` / production: `REACT_APP_NODE_API_URL` (or Omni base)
 */
function buildUrl(path: string): string {
  const base = jsonApiBaseUrl();
  const p = path.startsWith("/") ? path : `/${path}`;
  if (shouldUseDevProxyForJsonApi(base)) {
    return p;
  }
  return base ? `${base}${p}` : p;
}

function parseJsonError(data: unknown): { message: string; code: string } {
  if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    const nested =
      o.error && typeof o.error === "object" && !Array.isArray(o.error)
        ? (o.error as Record<string, unknown>)
        : null;
    const message =
      (nested && typeof nested.message === "string" && nested.message) ||
      (typeof o.message === "string" && o.message) ||
      (Array.isArray(o.errors) &&
        o.errors[0] &&
        typeof (o.errors[0] as { message?: string }).message === "string" &&
        (o.errors[0] as { message: string }).message) ||
      (typeof o.detail === "string" && o.detail) ||
      (typeof o.error === "string" && o.error) ||
      "Request failed";
    const code =
      (nested && typeof nested.code === "string" && nested.code) ||
      (typeof o.code === "string" && o.code) ||
      (typeof o.error === "string" && o.error) ||
      "REQUEST_FAILED";
    return { message, code };
  }
  return { message: "Request failed", code: "REQUEST_FAILED" };
}

export async function apiGet<T>(path: string): Promise<T> {
  const url = buildUrl(path);
  const token = tokenManager.getToken();
  const res = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...userTimezoneHeaders(),
    },
  });
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const { message, code } = parseJsonError(data);
    if (handleAuthApiFailure(res.status, code, message, data)) {
      throw new ApiRequestError(message, code, res.status);
    }
    throw new ApiRequestError(message, code, res.status);
  }
  return data as T;
}

/**
 * GET JSON from a fully qualified URL (e.g. Node API on another origin).
 * Uses the same auth and error handling as {@link apiGet}.
 */
export async function apiGetAbsoluteUrl<T>(fullUrl: string): Promise<T> {
  const token = tokenManager.getToken();
  const res = await fetch(fullUrl, {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...userTimezoneHeaders(),
    },
  });
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const { message, code } = parseJsonError(data);
    if (handleAuthApiFailure(res.status, code, message, data)) {
      throw new ApiRequestError(message, code, res.status);
    }
    throw new ApiRequestError(message, code, res.status);
  }
  return data as T;
}

/** POST/PATCH/PUT/DELETE JSON at a fully qualified URL (same auth/errors as {@link apiSend}). */
export async function apiSendAbsoluteUrl<T>(
  fullUrl: string,
  method: "POST" | "PATCH" | "PUT" | "DELETE",
  body?: unknown,
): Promise<T> {
  const token = tokenManager.getToken();
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...userTimezoneHeaders(),
  };
  const hasBody = body !== undefined;
  if (hasBody) headers["Content-Type"] = "application/json";
  const res = await fetch(fullUrl, {
    method,
    headers,
    body: hasBody ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const { message, code } = parseJsonError(data);
    if (handleAuthApiFailure(res.status, code, message, data)) {
      throw new ApiRequestError(message, code, res.status);
    }
    throw new ApiRequestError(message, code, res.status);
  }
  if (method === "DELETE" && (data === null || data === "")) {
    return { deleted: true } as T;
  }
  return data as T;
}

export async function apiGetRaw(
  path: string,
  init?: RequestInit,
): Promise<Response> {
  const url = buildUrl(path);
  const token = tokenManager.getToken();
  return fetch(url, {
    ...init,
    method: "GET",
    headers: {
      Accept: "*/*",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...userTimezoneHeaders(),
      ...(init?.headers && typeof init.headers === "object"
        ? init.headers
        : {}),
    },
  });
}

export async function apiSend<T>(
  path: string,
  method: "POST" | "PATCH" | "PUT" | "DELETE",
  body?: unknown,
): Promise<T> {
  const url = buildUrl(path);
  const token = tokenManager.getToken();
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...userTimezoneHeaders(),
  };
  const hasBody = body !== undefined && method !== "GET";
  if (hasBody) headers["Content-Type"] = "application/json";
  const res = await fetch(url, {
    method,
    headers,
    body: hasBody ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const { message, code } = parseJsonError(data);
    if (handleAuthApiFailure(res.status, code, message, data)) {
      throw new ApiRequestError(message, code, res.status);
    }
    throw new ApiRequestError(message, code, res.status);
  }
  if (method === "DELETE" && (data === null || data === "")) {
    return { deleted: true } as T;
  }
  return data as T;
}

export function getOmniWatchApiConfigured(): boolean {
  return getOmniWatchBaseUrl().length > 0;
}
